import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Loader2, Calculator, AlertTriangle, DollarSign } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Mono, Field, money, inpCls, selCls } from "./ui";

export function CostPanel({ asset, region }) {
  const cet = asset.cet_cost, council = asset.council_cost;
  const hasBoth = cet != null && council != null && cet;
  const diff = hasBoth ? council - cet : null;
  const pct = hasBoth ? (diff / cet) * 100 : null;
  const flagged = hasBoth && Math.abs(pct) >= 5 && !asset.cost_justification;
  if (cet == null && council == null) return null;
  return (
    <div className="border border-gold-500/20 bg-navy-900/40 p-4" data-testid="asset-cost-panel">
      <div className="flex items-center justify-between mb-3">
        <Mono className="text-[0.55rem] text-white/50 flex items-center gap-1.5"><Calculator className="h-3 w-3 text-gold-500/60" /> Cost — regionally verified benchmark vs Council</Mono>
        <span className="font-mono text-[0.5rem] text-white/35">{region} · {asset.unit_of_measure || "—"}{asset.quantity ? ` × ${asset.quantity}` : ""}</span>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="border border-gold-500/15 p-3"><Mono className="text-[0.45rem] text-white/40 block mb-1">CET benchmark</Mono><div className="font-mono text-gold-300 text-sm" data-testid="cost-cet">{money(cet)}</div></div>
        <div className="border border-gold-500/15 p-3"><Mono className="text-[0.45rem] text-white/40 block mb-1">Council cost</Mono><div className="font-mono text-white text-sm" data-testid="cost-council">{money(council)}</div></div>
        <div className="border border-gold-500/15 p-3"><Mono className="text-[0.45rem] text-white/40 block mb-1">Variance</Mono><div className={`font-mono text-sm ${diff > 0 ? "text-red-300" : diff < 0 ? "text-emerald-300" : "text-white/60"}`} data-testid="cost-variance">{diff == null ? "—" : `${money(diff)} (${pct >= 0 ? "+" : ""}${pct.toFixed(0)}%)`}</div></div>
      </div>
      {asset.cost_justification && (
        <div className="mt-3 border-l-2 border-gold-500/50 bg-navy-950/60 px-3 py-2 text-xs text-white/70" data-testid="cost-justification"><Mono className="text-[0.45rem] text-gold-400/70 block mb-1">Justification</Mono>{asset.cost_justification}</div>
      )}
      {flagged && (
        <div className="mt-3 flex items-center gap-2 text-xs text-amber-300" data-testid="cost-justification-warning"><AlertTriangle className="h-3.5 w-3.5" /> Council cost varies {Math.abs(pct).toFixed(0)}% from CET benchmark — add a justification for assessor review.</div>
      )}
    </div>
  );
}

export function CostItemsPanel({ assetId, claimId, meta, region }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const empty = { folder: "damage", treatment_code: "", description: "", unit: "ea", quantity: 1, unit_rate: 0 };
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get(`/fieldclaim/assets/${assetId}/costitems`).then((r) => setItems(r.data)).catch(() => {}), [assetId]);
  useEffect(() => { load(); setOpen(false); setForm(empty); }, [assetId, load]); // eslint-disable-line

  const onTreatment = (code) => {
    const t = meta.treatments.find((x) => x.code === code);
    if (!t) { setForm((f) => ({ ...f, treatment_code: "", description: "" })); return; }
    const rate = t.rates?.[region];
    setForm((f) => ({ ...f, treatment_code: code, description: t.name, unit: t.unit, unit_rate: rate != null ? rate : f.unit_rate, category: t.category }));
  };

  const total = (Number(form.quantity) || 0) * (Number(form.unit_rate) || 0);

  const add = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      await api.post(`/fieldclaim/assets/${assetId}/costitems`, {
        folder: form.folder, description: form.description, category: form.category,
        unit: form.unit, quantity: Number(form.quantity) || 0, unit_rate: Number(form.unit_rate) || 0,
      });
      setForm(empty); setOpen(false); load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || "Could not add cost item.");
    } finally { setSaving(false); }
  };

  const del = async (id) => { try { await api.delete(`/fieldclaim/costitems/${id}`); load(); } catch { /* noop */ } };
  const grand = items.reduce((s, it) => s + (it.total || 0), 0);
  const FOLDERS = meta.phases || [];

  return (
    <div className="border border-gold-500/20" data-testid="cost-items-panel">
      <div className="flex items-center justify-between px-4 py-3 bg-navy-900/60 border-b border-gold-500/15">
        <Mono className="text-[0.55rem] text-gold-400/80 flex items-center gap-1.5"><DollarSign className="h-3 w-3" /> Cost items · {money(grand)}</Mono>
        <button onClick={() => setOpen((o) => !o)} data-testid="add-cost-item-btn" className="inline-flex items-center gap-1.5 text-gold-400 hover:text-gold-300 text-xs"><Plus className="h-3.5 w-3.5" /> Add line</button>
      </div>

      {open && (
        <form onSubmit={add} className="p-4 border-b border-gold-500/15 space-y-3 bg-navy-950/40" data-testid="cost-item-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Folder">
              <select value={form.folder} onChange={(e) => setForm((f) => ({ ...f, folder: e.target.value }))} data-testid="ci-folder" className={selCls}>
                {FOLDERS.map((p) => <option key={p.code} value={p.code} className="bg-navy-900">{p.name}</option>)}
              </select>
            </Field>
            <Field label={`Treatment (${region})`}>
              <select value={form.treatment_code} onChange={(e) => onTreatment(e.target.value)} data-testid="ci-treatment" className={selCls}>
                <option value="" className="bg-navy-900">Custom / manual</option>
                {meta.treatments.map((t) => <option key={t.code} value={t.code} className="bg-navy-900">{t.name} {t.rates?.[region] != null ? `— $${t.rates[region]}/${t.unit}` : ""}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description">
            <input required value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="ci-description" className={inpCls} />
          </Field>
          <div className="grid grid-cols-4 gap-3 items-end">
            <Field label="Unit"><input value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} data-testid="ci-unit" className={inpCls} /></Field>
            <Field label="Qty"><input type="number" step="any" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} data-testid="ci-qty" className={inpCls} /></Field>
            <Field label="Rate ($)"><input type="number" step="any" value={form.unit_rate} onChange={(e) => setForm((f) => ({ ...f, unit_rate: e.target.value }))} data-testid="ci-rate" className={inpCls} /></Field>
            <div><Mono className="text-white/50 text-[0.5rem] block mb-1.5">Total</Mono><div className="font-mono text-gold-300 text-sm py-2.5" data-testid="ci-total">{money(total)}</div></div>
          </div>
          <button type="submit" disabled={saving} data-testid="save-cost-item-btn" className="w-full bg-gold-500 text-navy-950 font-semibold py-2.5 text-sm rounded-sm hover:bg-gold-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Add cost item</button>
          {err && <div className="text-red-300 text-xs" data-testid="cost-item-error">{err}</div>}
        </form>
      )}

      {items.length === 0 ? (
        <div className="px-4 py-6 text-center text-white/30 text-xs font-light">No cost line items yet.</div>
      ) : (
        <div className="divide-y divide-gold-500/10">
          {items.map((it) => (
            <div key={it.id} className="px-4 py-3 flex items-center gap-3" data-testid={`cost-item-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{it.description}</div>
                <Mono className="text-[0.5rem] text-white/40">{FOLDERS.find((p) => p.code === it.folder)?.name || it.folder} · {it.quantity} {it.unit} × {money(it.unit_rate)}</Mono>
              </div>
              <div className="font-mono text-gold-300 text-sm shrink-0">{money(it.total)}</div>
              <button onClick={() => del(it.id)} data-testid={`delete-cost-item-${it.id}`} className="text-white/25 hover:text-red-400 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
